import { fetchEventSource } from "@microsoft/fetch-event-source";
import { useEffect, useRef } from "react";
import { API_ENDPOINTS } from "@/shared/api";
import { env } from "@/shared/config";
import { getAuthToken } from "@/shared/utils/auth";
import { useWorkRoleStore } from "@/features/user/model/workRoleStore";

export interface UseRealtimeStreamOptions {
  /** Called whenever the backend pushes an `account_module_change` event. */
  onModuleChanged?: () => void;
  /** Called whenever the backend pushes a `permission_change` event. */
  onPermissionChanged?: () => void;
}

/**
 * Opens a single SSE connection to `GET /events/stream` and routes each
 * server-pushed event to the appropriate callback.
 *
 * Supported event types:
 *   - `account_module_change`  → onModuleChanged
 *   - `permission_change`      → onPermissionChanged
 *
 * To add a future event type, extend UseRealtimeStreamOptions with a new
 * callback and add the corresponding `if (event.event === '...')` branch below.
 *
 * The connection is only opened for non-SystemAdmin users and is automatically
 * torn down on unmount or when the admin status changes.
 */
export function useRealtimeStream({
  onModuleChanged,
  onPermissionChanged,
}: UseRealtimeStreamOptions = {}) {
  const isSystemAdmin = useWorkRoleStore((s) => s.isSystemAdmin());

  // Stable refs so callers don't need useCallback to avoid reconnects.
  const onModuleChangedRef = useRef(onModuleChanged);
  const onPermissionChangedRef = useRef(onPermissionChanged);
  useEffect(() => {
    onModuleChangedRef.current = onModuleChanged;
    onPermissionChangedRef.current = onPermissionChanged;
  });

  useEffect(() => {
    if (isSystemAdmin) return;

    const token = getAuthToken();
    if (!token) return;

    const controller = new AbortController();
    const url = `${env.api.url}${API_ENDPOINTS.ACCOUNTS.realtimeStream()}`;

    console.log("[SSE] Connecting to realtime stream…", url);

    fetchEventSource(url, {
      signal: controller.signal,
      headers: { Authorization: `Bearer ${token}` },
      openWhenHidden: true,

      onopen(response) {
        if (response.ok) {
          console.log("[SSE] Connection established (realtime stream)");
        } else {
          console.warn("[SSE] Unexpected response status:", response.status);
        }
        return Promise.resolve();
      },

      onmessage(event) {
        if (!event.event && !event.data) {
          console.log("[SSE] Keepalive / empty frame (ignored)");
          return;
        }


        if (event.event === "account_module_change") {
          onModuleChangedRef.current?.();
          return;
        }

        if (event.event === "permission_change") {
          onPermissionChangedRef.current?.();
          return;
        }
      },

      onclose() {
        console.log("[SSE] Connection closed (realtime stream)");
      },

      onerror(err) {
        console.error("[SSE] Stream error:", err);
        // Re-throw to stop the automatic retry loop on hard errors.
        throw err;
      },
    }).catch(() => {
      // AbortError on cleanup — nothing to do.
    });

    return () => {
      console.log("[SSE] Disconnecting realtime stream");
      controller.abort();
    };
  }, [isSystemAdmin]);
}
